import React from 'react';
import { QrCode, Award, CreditCard } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';

export function MembershipCard({ user, profile }) {
    const { organization } = useOrganization();
    const { user: authUser } = useAuth();
    
    // Determine if this is a resident or shopper
    const isResident = authUser?.user_type === 'residence';
    
    // Get organization branding
    const orgName = organization?.name || 'Member';
    const orgLogo = organization?.logo_url;
    const isElysian = organization?.slug === 'elysian' || orgName?.toLowerCase().includes('elysian');
    const isBurston = organization?.slug === 'burston' || orgName?.toLowerCase().includes('burston');
    
    // Determine card title based on org and user type
    const getCardTitle = () => {
        if (isElysian) return 'Elysian Resident';
        if (isBurston && isResident) return 'Burston Resident';
        if (isBurston) return 'Friend of Burston';
        return isResident ? 'Resident Member' : 'Member';
    };
    
    // Determine what balance to show
    const showCredits = isResident && profile?.credits_balance !== undefined;
    const balanceLabel = showCredits ? 'Credits Balance' : 'Points Balance';
    const balanceValue = showCredits ? (profile?.credits_balance || 0) : (profile?.points_balance || 0);
    
    return (
        <div className="membership-card rounded-3xl p-6 text-white shadow-xl" data-testid="membership-card">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    {orgLogo ? (
                        <img 
                            src={orgLogo}
                            alt={orgName}
                            className={`h-10 w-auto ${isElysian ? '' : 'brightness-0 invert'}`}
                        />
                    ) : (
                        <span className="text-xl font-serif">{orgName}</span>
                    )}
                </div>
                <span className="text-white/80 text-sm font-medium">{getCardTitle()}</span>
            </div>

            {/* Member Info */}
            <div className="flex justify-between items-end">
                <div>
                    <p className="text-white/70 text-sm mb-1">Member</p>
                    <p className="text-2xl font-bold mb-4">{user?.name || 'Member'}</p>
                    
                    <p className="text-white/70 text-sm mb-1">Membership Number</p>
                    <p className="font-mono text-lg tracking-wider">{user?.membership_number || 'XXXXXX'}</p>
                </div>

                {/* QR Code placeholder */}
                <div className="bg-white rounded-xl p-2" data-testid="qr-code">
                    <div className="w-20 h-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNTYgMjU2Ij48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0iI2ZmZiIvPjxnIGZpbGw9IiMwRjREM0YiPjxyZWN0IHg9IjMyIiB5PSIzMiIgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0Ii8+PHJlY3QgeD0iMTYwIiB5PSIzMiIgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0Ii8+PHJlY3QgeD0iMzIiIHk9IjE2MCIgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0Ii8+PHJlY3QgeD0iMTEyIiB5PSIxMTIiIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIvPjxyZWN0IHg9IjE2MCIgeT0iMTYwIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiLz48cmVjdCB4PSIxOTIiIHk9IjE5MiIgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIi8+PC9nPjwvc3ZnPg==')] bg-contain" />
                </div>
            </div>

            {/* Balance */}
            <div className="mt-6 pt-4 border-t border-white/20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {showCredits ? <CreditCard className="w-5 h-5" /> : <Award className="w-5 h-5" />}
                        <span className="text-white/80">{balanceLabel}</span>
                    </div>
                    <span className="text-2xl font-bold">{balanceValue.toLocaleString()}</span>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-4 text-center">
                <p className="text-white/60 text-sm">Show to staff at checkout</p>
            </div>
        </div>
    );
}

export default MembershipCard;
